import {
  GiftCardTransactionsFragment,
  Order_By,
  useInfiniteGiftCardTransactionListQuery,
  useTotalGiftCardQuery,
  useTotalGiftCardSoldQuery,
} from '@/gql/generated';
import { ErrorType } from '@/lib/handle-error';
import {
  convertDateToTimezone,
  formatDateToTimezone,
  getGMTOffsetByTimezone,
} from '@/lib/timezone';
import { normalizeTransactionInfinite } from '@/types/gift-card-transaction/transaction.dto';
import { TTotalSold } from '@/types/gift-card-transaction/transaction.type';
import { dateToPreviousDate, transactionsType } from '@/utils/constant';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { endOfDay } from 'date-fns';
import { createContext, useState } from 'react';
import { TZDate } from 'react-day-picker';
import { useAuthenticatedProvider } from './authenticated-provider';

export const useTotalGiftCardSold = (props: {
  whmcsId: number | undefined;
  startDate: TZDate;
  endDate: TZDate;
  timeZone: string;
}) => {
  const totalGiftCardSoldQuery = useTotalGiftCardSoldQuery({
    where: {
      whmcs_id: { _eq: props.whmcsId },
      sold_date: {
        _gte: formatDateToTimezone(
          props.startDate,
          props.timeZone,
          `yyyy-MM-dd'T'00:00:00'${getGMTOffsetByTimezone(props.timeZone)}'`,
        ),
        _lte: formatDateToTimezone(
          endOfDay(props.endDate),
          props.timeZone,
          `yyyy-MM-dd'T'23:59:59'${getGMTOffsetByTimezone(props.timeZone)}'`,
        ),
      },
    },
  });
  return {
    data: totalGiftCardSoldQuery.data?.gift_card_aggregate.aggregate?.count,
    isLoading: totalGiftCardSoldQuery.isLoading,
    refetch: totalGiftCardSoldQuery.refetch,
  } as TTotalSold;
};

export const useTotalGiftCard = (props: {
  whmcsId: number | undefined;
  startDate: TZDate;
  endDate: TZDate;
  type: string;
  timeZone: string;
}) => {
  const totalGiftCardQuery = useTotalGiftCardQuery({
    where: {
      whmcs_id: { _eq: props.whmcsId },
      type: { _eq: props.type },
      date: {
        _gte: formatDateToTimezone(
          props.startDate,
          props.timeZone,
          `yyyy-MM-dd'T'00:00:00'${getGMTOffsetByTimezone(props.timeZone)}'`,
        ),
        _lte: formatDateToTimezone(
          props.endDate,
          props.timeZone,
          `yyyy-MM-dd'T'23:59:59'${getGMTOffsetByTimezone(props.timeZone)}'`,
        ),
      },
    },
  });
  return {
    data: totalGiftCardQuery.data?.transaction_aggregate.aggregate?.sum
      ?.balance_change,
    isLoading: totalGiftCardQuery.isLoading,
    refetch: totalGiftCardQuery.refetch,
  } as TTotalSold;
};

type TTransactionWarehouseContext = {
  event: {
    setStartDate: (date: TZDate) => void;
    setEndDate: (date: TZDate) => void;
    refetchAll: () => void;
  };
  queryResult:
    | UseInfiniteQueryResult<GiftCardTransactionsFragment[], ErrorType>
    | undefined;

  totalGiftCardSold: TTotalSold | undefined;
  totalPreviousGiftCardSold: TTotalSold | undefined;
  totalRedeem: TTotalSold | undefined;
  totalPreviousRedeem: TTotalSold | undefined;
  totalAddFund: TTotalSold | undefined;
  totalPreviousAddFund: TTotalSold | undefined;
};

export const TransactionWarehouseContext =
  createContext<TTransactionWarehouseContext>({
    event: {
      setStartDate: () => {},
      setEndDate: () => {},
      refetchAll: () => {},
    },
    queryResult: undefined,
    totalGiftCardSold: undefined,
    totalPreviousGiftCardSold: undefined,
    totalRedeem: undefined,
    totalPreviousRedeem: undefined,
    totalAddFund: undefined,
    totalPreviousAddFund: undefined,
  });

export default function TransactionWarehouseProvider(props: {
  children?: React.ReactNode;
}) {
  const { user } = useAuthenticatedProvider();
  const pageSize = 20;
  // const options = [
  //   // {
  //   //   value: 'Day',
  //   //   label: 'Day',
  //   //   startDate: startOfDay(new Date()),
  //   //   endDate: endOfDay(new Date()),
  //   // },
  //   {
  //     value: 'Week',
  //     label: 'Week',
  //     startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
  //     endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
  //   },
  //   // {
  //   //   value: 'Month',
  //   //   label: 'Month',
  //   //   startDate: startOfMonth(new Date()),
  //   //   endDate: endOfMonth(new Date()),
  //   // },
  //   // {
  //   //   value: 'Year',
  //   //   label: 'Year',
  //   //   startDate: startOfYear(new Date()),
  //   //   endDate: endOfYear(new Date()),
  //   // },
  // ];
  const [startDate, setStartDate] = useState<TZDate>(
    convertDateToTimezone(new Date(), user.time_zone!),
  );
  const [endDate, setEndDate] = useState<TZDate>(
    convertDateToTimezone(new Date(), user.time_zone!),
  );
  const dateTimeFilter = dateToPreviousDate(startDate, endDate);

  const transactionsQuery = useInfiniteGiftCardTransactionListQuery(
    {
      offset: 0,
      limit: pageSize,
      orderBy: [{ date: Order_By.Desc }],
      where: {
        whmcs_id: { _eq: user.whmcs_id },
        date: {
          _gte: formatDateToTimezone(
            startDate,
            user.time_zone!,
            `yyyy-MM-dd'T'00:00:00'${getGMTOffsetByTimezone(user.time_zone!)}'`,
          ),
          _lte: formatDateToTimezone(
            endDate,
            user.time_zone!,
            `yyyy-MM-dd'T'23:59:59'${getGMTOffsetByTimezone(user.time_zone!)}'`,
          ),
        },
      },
    },
    {
      initialPageParam: { offset: 0 },
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.transaction.length < pageSize
          ? undefined
          : { offset: allPages.length * pageSize };
      },
      refetchOnWindowFocus: false,
      enabled: !!user.whmcs_id,
      select: normalizeTransactionInfinite,
    },
  );

  const totalGiftCardSold = useTotalGiftCardSold({
    whmcsId: user.whmcs_id,
    startDate: startDate,
    endDate: endDate,
    timeZone: user.time_zone!,
  });
  const totalPreviousGiftCardSold = useTotalGiftCardSold({
    whmcsId: user.whmcs_id,
    startDate: dateTimeFilter.start,
    endDate: dateTimeFilter.end,
    timeZone: user.time_zone!,
  });

  const totalRedeem = useTotalGiftCard({
    whmcsId: user.whmcs_id,
    startDate: startDate,
    endDate: endDate,
    type: transactionsType.REDEMPTION,
    timeZone: user.time_zone!,
  });
  const totalPreviousRedeem = useTotalGiftCard({
    whmcsId: user.whmcs_id,
    startDate: dateTimeFilter.start,
    endDate: dateTimeFilter.end,
    type: transactionsType.REDEMPTION,
    timeZone: user.time_zone!,
  });
  const totalAddFund = useTotalGiftCard({
    whmcsId: user.whmcs_id,
    startDate: startDate,
    endDate: endDate,
    type: transactionsType.TOP_UP,
    timeZone: user.time_zone!,
  });
  const totalPreviousAddFund = useTotalGiftCard({
    whmcsId: user.whmcs_id,
    startDate: dateTimeFilter.start,
    endDate: dateTimeFilter.end,
    type: transactionsType.TOP_UP,
    timeZone: user.time_zone!,
  });

  const onRefetchAll = () => {
    transactionsQuery.refetch();
    totalGiftCardSold.refetch();
    totalPreviousGiftCardSold.refetch();
    totalRedeem.refetch();
    totalPreviousRedeem.refetch();
    totalAddFund.refetch();
    totalPreviousAddFund.refetch();
  };

  return (
    <TransactionWarehouseContext.Provider
      value={{
        event: {
          setStartDate,
          setEndDate,
          refetchAll: onRefetchAll,
        },
        queryResult: transactionsQuery,
        totalGiftCardSold,
        totalPreviousGiftCardSold,
        totalRedeem,
        totalPreviousRedeem,
        totalAddFund,
        totalPreviousAddFund,
      }}
    >
      {props.children}
    </TransactionWarehouseContext.Provider>
  );
}
